/**
 * Papel fiscal preimpreso (spec 05, enmienda 2026-10-09): la hoja trae el número de control impreso.
 *  - Hoja dañada CON documento: se anulan su número y su control y se reemite con los siguientes (mismo día, caja abierta).
 *  - Hoja EN BLANCO dañada: se pierde solo el número de control.
 * Ambas piden permiso `ventas.anular` o la autorización de quien lo tiene (clave en pantalla o a distancia).
 */
import type { DocumentoDTO } from '@mg/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { ErrorApi, mensajeDeError } from '@/lib/api.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { anularControl, useControlesAnulados, useReemitir } from './api.ts';

export function DialogoReemitir({ doc, abierto, cerrar }: { doc: DocumentoDTO; abierto: boolean; cerrar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [clave, setClave] = useState(() => crypto.randomUUID());
  // Otro documento, otra clave (hallazgo T6): quien la usa la monta con key={doc.id}, así cada documento empieza con la suya
  const [enviando, setEnviando] = useState(false);
  const reemitir = useReemitir(doc.id);
  const aut = useAutorizacion();
  const confirmar = async () => {
    setEnviando(true);
    try {
      const r = await aut.ejecutar((ids) => reemitir.mutateAsync({ motivo: motivo.trim(), autorizaciones: ids, clave, numero_actual: doc.numero_formateado }), {
        motivo: motivo.trim(),
        detalle: { documento: `${doc.tipo} ${doc.numero_formateado}`, control: doc.control_formateado ?? '', cliente: doc.cliente_razon_social },
        montoUsd: doc.total,
      });
      if (r) {
        mostrarRegistrado({
          titulo: `${doc.numero_formateado} anulada · reemitida como ${r.numero_formateado}`,
          descripcion: `Nuevo N° de control ${r.control_formateado} · ${r.cliente_razon_social}`,
          detalle: [{ texto: 'Coloca la hoja siguiente en la impresora e imprime.' }],
          imprimir: [{ etiqueta: 'Imprimir', ruta: `/imprimir/${r.tipo}/${r.id}` }],
        });
        aut.limpiar();
        setMotivo('');
        setClave(crypto.randomUUID());
        cerrar();
      }
    } catch (e) {
      // Otra pantalla ya lo reemitió (H-15): se cierra este diálogo, que todavía dice el número viejo, y el aviso se queda
      if (e instanceof ErrorApi && e.codigo === 'documento_ya_reemitido') {
        toast.error('Este documento ya se reemitió en otra pantalla', { description: mensajeDeError(e), duration: 20000 });
        setMotivo('');
        cerrar();
      } else toast.error(mensajeDeError(e));
      setClave(crypto.randomUUID());
    } finally {
      setEnviando(false);
    }
  };
  return (
    <>
      <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent data-testid="dialogo-reemitir">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Anular y reemitir {doc.numero_formateado}</DialogTitle><AyudaVentana tema="documentos.reemitir" /></div>
            <DialogDescription>
              Para cuando la hoja se dañó o salió mal impresa. El número {doc.numero_formateado} y el control {doc.control_formateado} quedan ANULADOS (salen en el libro en cero) y el mismo documento
              —con sus renglones, cobros y entregas— toma los números siguientes. Solo el mismo día y con la caja abierta.
            </DialogDescription>
          </DialogHeader>
          <Campo etiqueta="¿Qué pasó con la hoja?" htmlFor="reemitir-motivo">
            <Textarea id="reemitir-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. la hoja se atascó en la impresora" data-testid="reemitir-motivo" />
          </Campo>
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Volver</Button>
            <Button variant="destructive" onClick={() => void confirmar()} cargando={enviando} disabled={motivo.trim().length < 5} data-testid="reemitir-confirmar">Anular y reemitir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {aut.dialogo}
    </>
  );
}

export function DialogoHojaEnBlanco({ abierto, cerrar }: { abierto: boolean; cerrar: () => void }) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState('');
  // Una clave por hoja: el doble clic o el reintento anulan una sola (hallazgo T13); se renueva cuando sale bien o falla
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const [enviando, setEnviando] = useState(false);
  const anulados = useControlesAnulados(abierto);
  const aut = useAutorizacion();
  const confirmar = async () => {
    setEnviando(true);
    try {
      const r = await aut.ejecutar((ids) => anularControl(motivo.trim(), ids, clave), { motivo: motivo.trim(), detalle: { operacion: 'Anular una hoja en blanco del talonario fiscal' } });
      if (r) {
        toast.success(`N° de control ${r.control_formateado} anulado`, { description: 'Guarda la hoja dañada con el talonario. La próxima factura toma el control siguiente.' });
        aut.limpiar();
        setMotivo('');
        setClave(crypto.randomUUID());
        void qc.invalidateQueries({ queryKey: ['ventas', 'controles-anulados'] });
      }
    } catch (e) {
      toast.error(mensajeDeError(e));
      if (e instanceof ErrorApi && e.status < 500) setClave(crypto.randomUUID());
    } finally {
      setEnviando(false);
    }
  };
  return (
    <>
      <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent data-testid="dialogo-hoja-blanco">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Anular una hoja en blanco</DialogTitle><AyudaVentana tema="documentos.hoja-blanco" /></div>
            <DialogDescription>
              Para cuando una hoja del talonario fiscal se daña SIN haber impreso nada (atasco, mancha, corte de luz). Se pierde su número de control —el siguiente que le toca a la sucursal— y el sistema
              sigue coincidiendo con el papel. Si la hoja tenía una factura impresa, anúlala desde el detalle de esa factura.
            </DialogDescription>
          </DialogHeader>
          <Campo etiqueta="¿Qué pasó con la hoja?" htmlFor="hoja-motivo">
            <Textarea id="hoja-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. la hoja salió manchada de tinta" data-testid="hoja-motivo" />
          </Campo>
          {anulados.data && anulados.data.length > 0 ? (
            <div className="max-h-40 overflow-auto rounded-lg border p-2 text-xs" data-testid="hojas-anuladas">
              <p className="mb-1 font-medium">Hojas anuladas</p>
              {anulados.data.map((a) => <p key={a.id} className="flex justify-between gap-2"><span><b>{a.control_formateado}</b> · {a.motivo}</span><span className="text-muted-foreground">{formatearFechaHora(a.anulado_en)} · {a.usuario_nombre}</span></p>)}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Cerrar</Button>
            <Button variant="destructive" onClick={() => void confirmar()} cargando={enviando} disabled={motivo.trim().length < 5} data-testid="hoja-confirmar">Anular el control</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {aut.dialogo}
    </>
  );
}
